import { ANIMATION, BORDERS, COLORS, SPACING } from '@alto/design-library-tokens';
import { AltoIcon, ListDescription, ListItem, MdSpacing, Row, useScreenSize } from '@alto/design-system';
import { BackorderedTag, useBackorderedMedication } from '@alto/features';
import React from 'react';
import styled from 'styled-components';
import { type OriginName } from '~shared/lib/analytics/src/getOrigin';
import { useNavigateToMedDetails } from '~web/hooks/useNavigateToMedDetails';
import IconPill from '~web/images/bluePill.svg';

const PrescriptionInfoContainer = styled.div`
  cursor: pointer;
  padding: ${SPACING.STATIC.MD.px};
  margin-left: -${SPACING.STATIC.MD.px};
  margin-right: -${SPACING.STATIC.MD.px};
  transition: background-color ${ANIMATION.SPEEDS.FAST}ms;
  border-radius: ${BORDERS.RADIUS.LG.px};

  &:hover {
    background-color: ${COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST};
  }
`;

const Img = styled.img`
  width: ${SPACING.STATIC.XL.px};
  height: ${SPACING.STATIC.XL.px};
  border-radius: ${BORDERS.RADIUS.MD.px};
`;

export type Props = {
  readonly byline?: string;
  readonly CTA?: React.ReactElement;
  readonly description?: string;
  readonly imageURL?: string;
  readonly isCTAButton?: boolean;
  readonly prescriptionID?: number;
  readonly origin: OriginName;
  readonly title: string;
};

export type Mutable<Type> = {
  -readonly [Key in keyof Type]: Type[Key];
};

export const PrescriptionInfo = ({
  byline,
  CTA,
  description,
  imageURL,
  isCTAButton,
  prescriptionID,
  origin,
  title,
}: Props) => {
  const { isSMScreenOrBigger } = useScreenSize();

  const navigateToMedDetails = useNavigateToMedDetails();

  const handlePrescriptionInfoClick = () => navigateToMedDetails(prescriptionID, origin);

  const isSmallerScreenOrBigger = isSMScreenOrBigger;

  const shouldShowButton = () => {
    if (isCTAButton && !isSmallerScreenOrBigger) return true;
    // eslint-disable-next-line sonarjs/prefer-single-boolean-return
    if (isCTAButton && isSmallerScreenOrBigger) return false;
    return true;
  };

  const showButton = shouldShowButton();

  const isBackorderdMedication = useBackorderedMedication(prescriptionID);

  if (isBackorderdMedication) {
    return (
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      <PrescriptionInfoContainer onClick={handlePrescriptionInfoClick}>
        <ListItem
          fullBleed
          title={title}
          byline={byline}
          tagPlacement="bottom"
          tags={[
            <BackorderedTag
              key="backordered-tag"
              origin="Home"
              prescriptionId={prescriptionID}
            />,
          ]}
          LeftContent={<Img src={imageURL || IconPill} />}
        />
      </PrescriptionInfoContainer>
    );
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <PrescriptionInfoContainer onClick={handlePrescriptionInfoClick}>
      <ListItem
        fullBleed
        title={title}
        byline={byline}
        descriptions={description ? [<ListDescription key={description}>{description}</ListDescription>] : undefined}
        BottomButton={showButton ? CTA : undefined}
        RightContent={
          <Row
            centerVertically
            wrap={false}
          >
            {isCTAButton && isSmallerScreenOrBigger ? (
              <Row wrap={false}>
                {CTA}
                <MdSpacing />
              </Row>
            ) : null}
            <AltoIcon
              name="chevronright"
              type="grey"
            />
          </Row>
        }
        LeftContent={<Img src={imageURL || IconPill} />}
      />
    </PrescriptionInfoContainer>
  );
};
